import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { ResourceStarButton } from '../../favorite-buttons/resource-star-button';
import { TopicTag } from '../../topic-tag';
import { Resource } from './useResourceModalData';
import { useTagGroups } from '../../../hooks/api/useTagGroups';
import { ResourceDescription } from './resource-description';
import { getResourceDescription } from '../../../utils/resource-content';

const PROVIDER_CONTENT_SANITIZE = {
  ALLOWED_TAGS: [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'p',
    'strong',
    'em',
    'u',
    's',
    'a',
    'ul',
    'ol',
    'li',
    'blockquote',
    'br',
  ],
  ALLOWED_ATTR: ['href', 'class', 'rel', 'target'],
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
};

interface ResourceModalContentProps {
  resource: Resource;
  isProviderResource: boolean;
  contentHtml?: string;
  resourceProp?: Resource;
}

export const ResourceModalContent: React.FC<ResourceModalContentProps> = ({
  resource,
  isProviderResource,
  contentHtml,
  resourceProp,
}) => {
  const { data: topicTagGroups } = useTagGroups();
  const sanitizedContentHtml = useMemo(
    () => DOMPurify.sanitize(contentHtml ?? '', PROVIDER_CONTENT_SANITIZE),
    [contentHtml],
  );

  return (
    <div className="flex flex-col items-start gap-6 px-[23px] pt-[19px] md:flex-row">
      {/* Square tile: self-start so flex row stretch from tall copy doesn't override aspect-ratio */}
      <div className="relative aspect-square w-full max-w-[303px] shrink-0 self-start overflow-hidden rounded-xl border border-black/20 md:h-[303px] md:w-[303px]">
        {resource.image_url ? (
          <img
            src={resource.image_url}
            alt={resource.name || 'Resource'}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <p className="font-open-sans absolute inset-0 flex items-center justify-center text-sm font-normal leading-[1.5] text-gray-500">
            no image found
          </p>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-1">
        {/* Topic Tag */}
        <div className="flex items-center gap-4">
          {resource.tag_group_names &&
            resource.tag_group_names.length > 0 &&
            resource.tag_group_names.slice(0, 2).map((tagGroupName) => (
              <div className="flex items-center gap-2" key={tagGroupName}>
                <TopicTag tagGroupName={tagGroupName} tagGroups={topicTagGroups} />
              </div>
            ))}
        </div>
        {/* Title and Star Button */}
        <div className="flex items-center gap-3">
          <h2 className="font-albert-sans text-label-gray min-w-0 flex-1 text-xl font-medium leading-7">
            {resource.name}
          </h2>
          {!isProviderResource && resourceProp && (
            <div className="flex-shrink-0">
              <ResourceStarButton resource={resourceProp} />
            </div>
          )}
        </div>

        {/* Description */}
        {isProviderResource ? (
          <div
            className="font-open-sans [&_a]:text-button-primary text-sm font-normal leading-[1.5] text-black [&_a]:underline [&_p]:mb-4 [&_ul]:mb-4 [&_ul]:ml-[21px] [&_ul]:list-disc"
            dangerouslySetInnerHTML={{ __html: sanitizedContentHtml }}
          />
        ) : (
          <ResourceDescription content={getResourceDescription(resource.long_description, resource.description)} />
        )}
      </div>
    </div>
  );
};
